import debounce from '../../lib/debounce.mjs';
import { requestProxy } from '../../lib/request-proxy.mjs';
import { bufferToString, stringToBuffer } from '../../utils/buffer.mjs';
import { MIN_PASSWORD_LENGTH } from '../../utils/const/passwords.mjs';
import {
  hasLowercase,
  hasMinCharacters,
  hasNumber,
  hasSymbol,
  hasUppercase,
} from '../../utils/passwords.mjs';

const FORM_TRACKING_FIELDS = [
  'company',
  'demo',
  'email',
  'first_contact',
  'name',
  'newsletter',
  'terms',
];

const EXTERNAL_SIGNUP_FIELDS = ['terms', 'demo', 'newsletter'];

function generatePasswordState() {
  return {
    hasMinCharacters: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSymbol: false,
  };
}

class Component {
  onCreate(input) {
    let defaultSignupMethod;

    const {
      error,
      githubAuthHref,
      googleAuthHref,
      hasPasskeysSignup,
      hasPasskeysSupport,
      qualcommAuthHref,
      shellAuthHref,
      signupAuthUrl,
      withInvite,
    } = input;

    const hasExternalLogin =
      githubAuthHref || googleAuthHref || qualcommAuthHref || shellAuthHref;

    const canUsePasskeys = hasPasskeysSupport && hasPasskeysSignup;
    if (canUsePasskeys) {
      defaultSignupMethod = 'passkey';
    } else {
      if (!canUsePasskeys && hasExternalLogin) {
        defaultSignupMethod = 'external';
      } else {
        defaultSignupMethod = 'local';
      }
    }

    this.state = {
      error,
      withInvite,
      defaultSignupMethod,
      signupAuthUrl,
      formData: {},
      isLoading: false,
      signupMethod: defaultSignupMethod,
      step: 0,
      isPasswordPwned: false,
      passwordState: generatePasswordState(),
      passwordFieldVisibility: {
        password: false,
      },
    };

    this.notification = null;
  }

  onMount() {
    this.notification = this.getComponent('notification');
    this.form = this.getEl('form');

    this.notifyError();
  }

  notifyError() {
    const { error } = this.state;
    if (error) {
      this.notification.onError(error);
    }
  }

  reset() {
    const { defaultSignupMethod } = this.state;
    this.setState({
      error: null,
      formData: {},
      isLoading: false,
      signupMethod: defaultSignupMethod,
      step: 0,
      isPasswordPwned: false,
      passwordState: generatePasswordState(),
    });
    this.form.reset();
  }

  async handleSignup(data) {
    const { signupAuthUrl } = this.state;

    const proxy = requestProxy();

    try {
      const response = await proxy.post({ path: signupAuthUrl, data });
      if (response.redirected) {
        window.location.assign(
          response.headers.get('location') || '/factories',
        );
        return;
      }
    } catch (ex) {
      this.notification.onError(ex);
      this.reset();
    }
  }

  async handleFormSubmit(event, form) {
    event.preventDefault();
    event.stopPropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (this.state.signupMethod !== 'local') {
      return;
    }

    if (form.password.value !== form['password-confirm'].value) {
      form['password-confirm'].setCustomValidity('Passwords do not match.');
      form.reportValidity();

      return;
    }

    const data = new window.FormData(form);
    data.set('password', window.btoa(encodeURIComponent(data.get('password'))));
    data.set(
      'password-confirm',
      window.btoa(encodeURIComponent(data.get('password-confirm'))),
    );

    setTimeout(() => {
      this.setState('isLoading', true);
    }, 0);

    await this.handleSignup(data);
  }

  async handlePasskeyCreation(credOptions) {
    let location;
    let publicKeyCredential;

    const { challenge, user } = credOptions;

    const options = structuredClone(credOptions);

    options.challenge = stringToBuffer(challenge);
    options.user.id = stringToBuffer(user.id);

    const proxy = requestProxy();

    try {
      publicKeyCredential = await navigator.credentials.create({
        publicKey: options,
      });
    } catch (ex) {
      await proxy.delete({
        path: `/auth/local/passkey/${user.id}`,
      });

      switch (ex.name) {
        case 'NotAllowedError':
          this.notification.notify({
            message: 'Passkey sign up timed out or canceled by the user',
          });
          break;
        case 'SecurityError':
          this.notification.notify({
            message:
              'Singup cannot continue: the web browser is prohibiting access to the passkey',
          });
          break;
        default:
          console.error(ex);
          this.notification.onError({
            error_description_html:
              'Singup cannot continue: error generating passkey.<br>Reload the page and try again.',
          });
          break;
      }

      this.reset();

      return;
    }

    if (!publicKeyCredential) {
      await proxy.delete({
        path: `/auth/local/passkey/${user.id}`,
      });

      this.notification.onError({
        error_description_html:
          'Singup cannot continue: no passkey has been generated.<br>Reload the page and try again.',
      });

      this.reset();

      return;
    }

    try {
      const data = new window.FormData();

      const { response, id, type } = publicKeyCredential;
      const { clientDataJSON, attestationObject } = response;

      const publicKey = response.getPublicKey();

      data.set('attestation', btoa(bufferToString(attestationObject)));

      if (clientDataJSON) {
        const dataJSONString = bufferToString(clientDataJSON);
        const dataJSON = JSON.parse(dataJSONString);

        const { challenge, origin, crossOrigin } = dataJSON;

        data.set('challenge', challenge);
        data.set('origin', origin);
        data.set('crossOrigin', crossOrigin);
      }

      data.set('id', id);
      data.set('type', type);

      data.set('publicKey', btoa(bufferToString(publicKey)));
      data.set('algo', response.getPublicKeyAlgorithm());

      for (const transport of response.getTransports()) {
        data.append('transports[]', transport);
      }

      const signupResponse = await proxy.post({
        path: `/auth/local/passkey/${user.id}`,
        data,
      });

      location = '/factories';
      if (signupResponse.redirected) {
        location = signupResponse.headers.get('location') || '/factories';
      }

      window.location.assign(location);
    } catch (ex) {
      await proxy.delete({
        path: `/auth/local/passkey/${user.id}`,
      });

      console.error(ex);

      this.notification.onError(ex);

      this.reset();
    }
  }

  async handlePasskeyClick(event) {
    event.stopPropagation();
    event.preventDefault();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    if (this.state.signupMethod !== 'passkey') {
      return;
    }

    try {
      const data = new window.FormData(this.form);

      const proxy = requestProxy();

      setTimeout(() => {
        this.setState('isLoading', true);
      }, 0);

      const response = await proxy.post({ path: '/auth/local/passkey', data });
      const { options } = response;

      await this.handlePasskeyCreation(options);
    } catch (ex) {
      this.notification.onError(ex);
      this.reset();
    }
  }

  onSocialLoginClick(provider, event, element) {
    event.preventDefault();
    event.stopPropagation();

    try {
      let signupElement = element;
      if (!signupElement) {
        signupElement = document.querySelector(
          '[data-signup="' + provider + '"]',
        );
      }

      if (!signupElement) {
        console.error('Cannot find social signup element');

        this.notification.onError({
          error_description:
            'Cannot sign up at this time. Reload the page and try again.',
        });
        return;
      }

      if (signupElement.disabled || this.state.signupMethod !== 'external') {
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setState('isLoading', true);

      const url = new URL(signupElement.href, window.location.origin);
      url.searchParams.append('from', window.location.pathname);
      url.searchParams.append('signup', 1);

      for (const field of EXTERNAL_SIGNUP_FIELDS) {
        // eslint-disable-next-line security/detect-object-injection
        if (this.form[field]) {
          // eslint-disable-next-line security/detect-object-injection
          url.searchParams.append(field, this.form[field].checked ? 1 : 0);
        }
      }

      url.searchParams.set('with_invite', this.form.with_invite.value);
      url.searchParams.set('country', window.btoa(this.form.country.value));
      url.searchParams.set('company', window.btoa(this.form.company.value));

      if (this.form.first_contact) {
        url.searchParams.set(
          'first_contact',
          window.btoa(this.form.first_contact.value),
        );
      }

      url.searchParams.append('email', window.btoa(this.form.email.value));
      url.searchParams.append(
        'signup-method',
        window.btoa(this.form['signup-method'].value),
      );

      window.location.replace(url.href);
    } catch (ex) {
      console.error(ex);

      this.notification.onError({
        error_description:
          'Cannot sign up at this time. Reload the page and try again.',
      });

      this.reset();
    }
  }

  handleSignupMethodClick(event) {
    const { target } = event;

    const { nodeName, type, dataset } = target;

    if (
      nodeName.toLowerCase() === 'input' &&
      type === 'radio' &&
      dataset.signupMethod
    ) {
      event.stopPropagation();
      event.preventDefault();

      const { signupMethod } = this.state;

      if (signupMethod !== dataset.signupMethod) {
        this.setState('signupMethod', dataset.signupMethod);
      }
      return;
    }
  }

  handleSignupNextBackClick(event) {
    const { target } = event;

    const { nodeName, dataset } = target;

    if (nodeName.toLowerCase() === 'button' && dataset.action) {
      event.stopPropagation();
      event.preventDefault();

      this.form['password-confirm']?.setCustomValidity('');
      this.form['password']?.setCustomValidity('');

      if (dataset.action === 'next') {
        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }
        this.setState({
          step: 1,
          passwordState: generatePasswordState(),
          isPasswordPwned: false,
        });
      } else {
        this.setState('step', 0);
      }

      return;
    }
  }

  handlePasswordFieldsetClicks(event) {
    const { target } = event;

    const { nodeName, dataset, role } = target;
    if (
      nodeName === 'SPAN' &&
      role === 'button' &&
      dataset.action === 'toggle' &&
      dataset.target
    ) {
      event.stopPropagation();
      event.preventDefault();

      const { passwordFieldVisibility } = this.state;

      passwordFieldVisibility[dataset.target] =
        !passwordFieldVisibility[dataset.target];
      this.setStateDirty('passwordFieldVisibility');
    }
  }
}

Component.prototype.handleSignupFormInput = debounce(function (event) {
  const { target } = event;

  const { name, value, checked, type } = target;
  if (FORM_TRACKING_FIELDS.includes(name)) {
    event.stopPropagation();

    const { formData } = this.state;

    if (type === 'checkbox') {
      // eslint-disable-next-line security/detect-object-injection
      formData[name] = Boolean(checked);
    } else {
      // eslint-disable-next-line security/detect-object-injection
      formData[name] = value;
    }
    return;
  }
}, 125);

Component.prototype.handlePasswordFieldsetInput = debounce(async function (
  event,
) {
  const { target } = event;
  const { name, value, nodeName } = target;

  this.form['password-confirm'].setCustomValidity('');

  if (nodeName === 'INPUT' && name === 'password') {
    const { passwordState } = this.state;

    passwordState.hasMinCharacters = hasMinCharacters(value);
    passwordState.hasUppercase = hasUppercase(value);
    passwordState.hasLowercase = hasLowercase(value);
    passwordState.hasNumber = hasNumber(value);
    passwordState.hasSymbol = hasSymbol(value);

    this.form.password.setCustomValidity('');

    setTimeout(() => {
      this.setState({
        isPasswordPwned: false,
      });
      this.setStateDirty('passwordState');
    }, 0);

    if (value.length >= MIN_PASSWORD_LENGTH) {
      const proxy = requestProxy();

      const data = new window.FormData();
      data.set('password', window.btoa(encodeURIComponent(value)));

      try {
        const response = await proxy.post({
          path: '/auth/local/password',
          data,
        });
        if (response) {
          const { pwned, message } = response;
          if (pwned) {
            this.form.password.setCustomValidity(message);
            this.setState({
              isPasswordPwned: true,
            });
          } else {
            this.form.password.setCustomValidity('');
            this.setState({
              isPasswordPwned: false,
            });
          }
        }
      } catch (ex) {
        console.error(ex);

        this.form.password.setCustomValidity('');
        this.setState({
          isPasswordPwned: false,
        });
      }
    }
  }
}, 275);

export default Component;
